import { useState } from 'react';

// Week view shows this many runway bands before folding the rest behind "+N more".
export const RUNWAY_STACK_MAX = 3;

// Month cells are narrow: cut the subject at its first " (" or " w/" so
// "Mark Daves — Green Span HQ (w/ ...)" reads "Mark Daves — Green Span HQ".
export function shortTitle(title) {
  const cut = title.search(/ \(| w\//);
  return cut > 0 ? title.slice(0, cut) : title;
}

// Band label: "Title — in 7 days" ... "Title — tomorrow" ... "Title" on the day.
// `full` keeps the whole subject (week view has the room).
export function runwayLabel(rw, full = false) {
  const t = full ? rw.event.title : shortTitle(rw.event.title);
  if (rw.daysRemaining === 0) return t;
  if (rw.daysRemaining === 1) return `${t} — tomorrow`;
  return `${t} — in ${rw.daysRemaining} days`;
}

// Escalating weight class by days remaining.
export function runwayTier(daysRemaining) {
  if (daysRemaining === 0) return 'day';
  if (daysRemaining === 1) return 'near';
  if (daysRemaining <= 3) return 'mid';
  return 'far';
}

// One band, shared by month cells and the week view. A span with button
// semantics because the month cell is itself a <button>.
export function RunwayBar({ rw, full = false, className = '', onOpen, more = 0 }) {
  return (
    <span
      role="button"
      tabIndex={0}
      className={`Centcom-runway Centcom-runway--${runwayTier(rw.daysRemaining)}${className ? ' ' + className : ''}`}
      title={runwayLabel(rw, true)}
      data-testid="runway-bar"
      onClick={e => { e.stopPropagation(); onOpen?.(rw); }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onOpen?.(rw); }
      }}
    >
      <span className="Centcom-runway-text">{runwayLabel(rw, full)}</span>
      {more > 0 && <span className="Centcom-runway-more">+{more}</span>}
    </span>
  );
}

// Week view: every runway covering the selected day as its own band, nearest
// first. Past RUNWAY_STACK_MAX the rest fold behind a slim "+N more" toggle.
// Expansion is local state; the parent keys this by day so it resets on a
// day change.
export default function RunwayStack({ runways, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  if (!runways || runways.length === 0) return null;
  const hidden = runways.length - RUNWAY_STACK_MAX;
  const shown = expanded || hidden <= 0 ? runways : runways.slice(0, RUNWAY_STACK_MAX);
  return (
    <div className="Centcom-runway-stack" data-testid="runway-stack">
      {shown.map(rw => (
        <RunwayBar key={rw.event.id} rw={rw} full className="Centcom-runway--week" onOpen={onOpen} />
      ))}
      {hidden > 0 && (
        <button
          type="button"
          className="Centcom-runway-toggle"
          data-testid="runway-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded(x => !x)}
        >
          {expanded ? 'Show less' : `+${hidden} more`}
        </button>
      )}
    </div>
  );
}
